import React, {
  useCallback, useState, useEffect, useContext, useMemo, useRef,
} from 'react';
import './ProductEditor.less';
import {
  Tabs, Form, Button, Tooltip, Tag,
} from 'antd';
import * as api from 'util/api.js';
import { useParams, useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import { TableConfigContext } from 'util/TableConfig';
import { AuthContext } from 'util/Auth';
import { PermissionsContext } from 'util/PermissionsContext';
import {
  stripProductSkuShortLabel,
  sortSkusByContainerType,
  getSkuContainerGroupLabel,
  renameSkuNameForProductRename,
} from 'util/sku-formatter';
import { extractPriceFields } from 'util/pricing-fields';
import EditorLayout from 'components/EditorLayout/EditorLayout';
import About from './tabs/About/index';
import ProductHistoryPanel from './ProductHistoryPanel';
import KeyDates from './tabs/KeyDates';
import Artwork from './tabs/Artwork';
import Extras from './tabs/Extras/index';
import Brewing from './tabs/Brewing/index';
import { MONDAY_PRODUCT_DATABASE_BOARD_ID } from '../MondayIntegration/constants';
import SkuManager from './tabs/Skus/index';
import PricingSummary from './tabs/PricingSummary';
import BarcodesSummary from './tabs/BarcodesSummary';

const { TabPane } = Tabs;

const { useForm } = Form;

const formTableHandler = (formValues) => {
  console.log('Product data: Working.', formValues);
  const productData = {};
  const skuData = [];
  Object.entries(formValues).forEach(([fieldName, value]) => {
    if (Array.isArray(value)) {
      if (fieldName === 'sku') {
        skuData.push(...value);
      }
    }
    if (fieldName !== 'sku') {
      productData[fieldName] = value;
    }
  });
  console.log('Product data: Product', productData);
  console.log('Product data: Sku', skuData);
  return { productData, skuData };
};

// Update table data if it exists by adding new product for faster subsequent loads.
const updateExistingTableData = (
  tableConfig,
  setTableConfig,
  id,
  {
    name,
    type,
    abv,
    abvRange,
    oneLiner,
    dateAdded,
    dateReleased,
    dateLastModified,
    complete,
    creativeReady,
    salesReady,
    productionReady,
    mondayId,
  },
) => {
  const { data: existingData } = tableConfig;

  if (existingData) {
    const newData = existingData.filter((product) => Number(product.id) !== Number(id));
    setTableConfig('product-database', {
      ...tableConfig,
      data: [
        ...newData,
        {
          key: id,
          id,
          mondayId,
          name,
          type,
          abv,
          oneLiner,
          dateAdded,
          dateReleased,
          dateLastModified,
          complete,
          creativeReady,
          salesReady,
          productionReady,
        },
      ],
    });
  }
};

/** Plain label text for SKU sub-tabs (extracted to avoid ESLint crash on nested JSX). */
function skuTabDisplayName(productName, sku) {
  const { name } = sku;
  if (typeof name !== 'string' || !name) return 'Unnamed SKU';
  const shortLabel = stripProductSkuShortLabel(name, productName || '');
  return shortLabel || 'Unnamed SKU';
}

/** First tab in each container group shows a group heading (Cans / Kegs / Bottles). */
function shouldShowSkuTabGroupHeader(sku, index, orderedSkus) {
  if (!orderedSkus?.length || index === 0) return true;
  return getSkuContainerGroupLabel(sku) !== getSkuContainerGroupLabel(orderedSkus[index - 1]);
}

const ProductEditor = () => {
  const { currentUser } = useContext(AuthContext);
  const { permissions } = useContext(PermissionsContext);
  const isAdmin = !!currentUser?.admin;
  const canEditSku = isAdmin || (Array.isArray(permissions) && permissions.includes('sku.edit'));
  const canViewActivity = isAdmin
    || (Array.isArray(permissions) && permissions.includes('activity.view'));

  const [initialValues, setInitialValues] = useState(null);
  const [savingStatus, setSavingStatus] = useState({
    active: false,
    error: false,
    unsavedChanges: false,
  });
  const [currentSkus, setCurrentSkus] = useState([]);
  const [activeSkuTab, setActiveSkuTab] = useState('new');
  const [activeEditorTab, setActiveEditorTab] = useState('about');
  const [productName, setProductName] = useState(null);

  const skusOrderedForTabs = useMemo(
    () => sortSkusByContainerType(currentSkus || []),
    [currentSkus],
  );

  const historySku = useMemo(() => {
    if (activeEditorTab !== 'skus' || activeSkuTab === 'new') return null;
    return (skusOrderedForTabs || []).find(
      (sku) => String(sku.key) === String(activeSkuTab),
    ) || null;
  }, [activeEditorTab, activeSkuTab, skusOrderedForTabs]);

  // TODO: Show warning if unsaved changes.
  // TODO: Delete uploaded images if declining to save changes and image has been uploaded.
  // const [unsavedChanges, setUnsavedChanges] = useState(false);

  const [productId, setProductId] = useState(null);

  const urlParams = useParams();
  const canSave = isAdmin || (!!urlParams.productId && canEditSku);

  const history = useHistory();

  const [form] = useForm();
  // Used to avoid references to form instance on first render.
  const [formInitialised, setFormInitialised] = useState(false);

  /** When false, suppresses unsaved dirty flag (hydration / programmatic setFieldsValue). */
  const formReady = useRef(false);
  const formReadyTimeoutRef = useRef(null);
  const mondayPollRef = useRef(null);

  /** Last persisted product name — used to cascade renames to SKU names on save. */
  const prevSavedNameRef = useRef(null);

  /** Snapshot of last-saved per-SKU prices, keyed by SKU key. Used for old-vs-new display. */
  const savedSkuPricesRef = useRef({});
  const snapshotSkuPrices = useCallback((skus) => {
    const map = {};
    (skus || []).forEach((s) => {
      if (s && s.key != null) {
        map[s.key] = extractPriceFields(s);
      }
    });
    savedSkuPricesRef.current = map;
  }, []);

  const scheduleFormReady = useCallback((delayMs = 2000) => {
    if (formReadyTimeoutRef.current) {
      clearTimeout(formReadyTimeoutRef.current);
    }
    formReadyTimeoutRef.current = setTimeout(() => {
      formReady.current = true;
      formReadyTimeoutRef.current = null;
    }, delayMs);
  }, []);

  const withFormSync = useCallback((fn) => {
    formReady.current = false;
    fn();
    scheduleFormReady(2000);
  }, [scheduleFormReady]);

  const clearMondayPoll = useCallback(() => {
    if (mondayPollRef.current) {
      clearInterval(mondayPollRef.current);
      mondayPollRef.current = null;
    }
  }, []);

  /** Poll until newly created SKUs receive mondayId from product-sync-handler write-back. */
  const startMondayIdPolling = useCallback((prodId, newSkuKeys) => {
    if (!newSkuKeys?.length) return;
    clearMondayPoll();
    let attempts = 0;
    mondayPollRef.current = setInterval(async () => {
      attempts += 1;
      // ~2 min. Write-back can land after the first autosave.
      if (attempts > 30) {
        clearMondayPoll();
        return;
      }
      try {
        const freshSkus = await api.getProductSkus(prodId);
        if (!freshSkus) return;
        const pendingStill = newSkuKeys.some((k) => {
          const row = freshSkus.find((s) => String(s.key) === String(k));
          return row && (row.mondayId == null || row.mondayId === '');
        });
        const mondayIdByKey = new Map(
          freshSkus
            .filter((s) => s.key != null && s.mondayId != null && s.mondayId !== '')
            .map((s) => [String(s.key), s.mondayId]),
        );
        const patchMondayId = (sku) => {
          const mondayId = mondayIdByKey.get(String(sku?.key));
          if (!mondayId || sku.mondayId === mondayId) return sku;
          return { ...sku, mondayId };
        };
        const current = form.getFieldValue('skus') || [];
        const patched = current.map(patchMondayId);
        if (patched.some((sku, i) => sku !== current[i])) {
          withFormSync(() => {
            form.setFieldsValue({
              ...form.getFieldsValue(),
              skus: patched,
            });
          });
        }
        setCurrentSkus((prev) => {
          const list = prev || [];
          const next = list.map(patchMondayId);
          return next.some((sku, i) => sku !== list[i]) ? next : prev;
        });
        if (!pendingStill) {
          clearMondayPoll();
        }
      } catch (e) {
        console.warn('Monday ID poll failed', e);
      }
    }, 4000);
  }, [clearMondayPoll, form, withFormSync]);

  // Product name and tap badge image URL must be stored locally so they can be used across tabs.
  const [tapBadgeImageUrl, setTapBadgeImageUrl] = useState();
  useEffect(() => {
    prevSavedNameRef.current = initialValues?.name ?? null;
    setProductName(initialValues?.name);
    setTapBadgeImageUrl(initialValues?.tapBadgeUrl || null);
    setCurrentSkus(initialValues?.skus || []);
    if (initialValues?.skus && initialValues.skus.length > 0) {
      const ordered = sortSkusByContainerType(initialValues.skus);
      setActiveSkuTab(ordered[0].key.toString());
    } else {
      setActiveSkuTab('new');
    }
  }, [initialValues]);

  const { getTableConfig, setTableConfig } = useContext(TableConfigContext);
  const tableConfig = getTableConfig('product-database');

  // Fetch and set fields when a product ID is provided or changes in URL params.
  useEffect(() => {
    let cancelled = false;
    formReady.current = false;
    if (formReadyTimeoutRef.current) {
      clearTimeout(formReadyTimeoutRef.current);
      formReadyTimeoutRef.current = null;
    }

    (async () => {
      const { productId: id } = urlParams;

      // TODO: Handle invalid ID provided in URL params.
      setProductId(id || null);

      const initialProductData = id ? await api.getProduct(id) : null;
      const initialProductSkuData = id ? await api.getProductSkus(id) : null;
      console.log('INTP: Initial', initialProductData);

      const parsedArtists = !!initialProductData && initialProductData.artist && typeof initialProductData.artist === 'string'
        ? initialProductData.artist.split(',').map((artist) => artist.trim())
        : [];

      snapshotSkuPrices(initialProductSkuData);
      setInitialValues(initialProductData ? {
        ...initialProductData,
        artist: parsedArtists,
        abvIsRange: initialProductData.abvMin !== null || initialProductData.abvMax !== null,
        skus: initialProductSkuData || [],
      } : ({
        dateAdded: dayjs(),
        artist: [],
        abvIsRange: false,
        skus: [],
      }));

      if (formInitialised) {
        form.resetFields();
      } else {
        setFormInitialised(true);
      }

      if (!cancelled) {
        scheduleFormReady(2000);
      }
    })();

    return () => {
      cancelled = true;
      if (mondayPollRef.current) {
        clearInterval(mondayPollRef.current);
        mondayPollRef.current = null;
      }
    };
    // formInitialised intentionally omitted — only urlParams.productId should refetch
  }, [urlParams.productId, form, scheduleFormReady]);

  useEffect(() => () => {
    if (formReadyTimeoutRef.current) {
      clearTimeout(formReadyTimeoutRef.current);
    }
    if (mondayPollRef.current) {
      clearInterval(mondayPollRef.current);
      mondayPollRef.current = null;
    }
  }, []);

  const onSave = (values) => {
    console.log('INTP: VALUES', values);
    setSavingStatus((current) => ({
      ...current,
      active: true,
      error: false,
      lastSaved: null,
    }));
    const {
      productData: rawProductData,
      skuData,
    } = formTableHandler(values);

    const {
      artist, abvIsRange, abv, abvMin, abvMax, sku, ...remainingValues
    } = rawProductData;
    console.log('INTP: Product data: unprocecessed:', values);
    console.log('INTP: Product data: processed', rawProductData);

    const productData = {
      ...remainingValues,
      artist: artist.length > 0 ? artist.join(', ') : null,
      ...(abvIsRange ? {
        abv: null,
        abvMin: abvMin ?? null,
        abvMax: abvMax ?? null,
      } : {
        abv: abv ?? null,
        abvMin: null,
        abvMax: null,
      }),
    };

    if (productId) {
      const previousProductName = prevSavedNameRef.current;
      const productNameChanged = productData.name !== previousProductName;
      const keysBeforeSave = new Set(
        (values.skus || []).filter((s) => s && s.key != null).map((s) => String(s.key)),
      );
      const existingSkus = productData.skus.some((existingSku) => !existingSku.key);
      api.updateProduct(productId, productData)
        .then(() => {
          if (productData.skus && productData.skus.length > 0) {
            const skusToSave = productNameChanged
              ? productData.skus.map((skuRow) => {
                const renamed = renameSkuNameForProductRename(
                  previousProductName,
                  productData.name,
                  skuRow.name,
                );
                return renamed != null ? { ...skuRow, name: renamed } : skuRow;
              })
              : productData.skus;
            console.log('SKU: Form', skusToSave);
            return api.updateProductSkus(productId, skusToSave);
          }
          return Promise.resolve(); // No SKUs to update
        })
        .then(async () => {
          // Refetch SKU data to get updated keys for new SKUs
          const updatedSkuData = await api.getProductSkus(productId);
          snapshotSkuPrices(updatedSkuData);

          // Update form with fresh SKU data (includes new keys from server)
          const currentFormValues = form.getFieldsValue();
          withFormSync(() => {
            form.setFieldsValue({
              ...currentFormValues,
              skus: updatedSkuData || [],
            });

            // Update the SKU tabs state
            setCurrentSkus(updatedSkuData || []);
          });

          const newSkuKeys = (updatedSkuData || []).filter(
            (s) => s.key && !keysBeforeSave.has(String(s.key)),
          ).map((s) => s.key);
          const needsMondayPoll = newSkuKeys.some((k) => {
            const row = updatedSkuData.find((s) => String(s.key) === String(k));
            return row && (row.mondayId == null || row.mondayId === '');
          });
          if (needsMondayPoll && newSkuKeys.length) {
            startMondayIdPolling(productId, newSkuKeys);
          }

          // If we had new SKUs being created, switch to the newest one
          if (existingSkus && updatedSkuData && updatedSkuData.length > 0) {
            // Find the newest SKU by highest key (most recently created)
            const newestSku = updatedSkuData.reduce(
              (latest, skuItem) => (!latest || skuItem.key > latest.key ? skuItem : latest),
              null,
            );
            if (newestSku) {
              setActiveSkuTab(newestSku.key.toString());
            }
          }

          prevSavedNameRef.current = productData.name ?? null;
          updateExistingTableData(tableConfig, setTableConfig, productId, productData);
          setSavingStatus({
            active: false,
            error: false,
            unsavedChanges: false,
            lastSaved: dayjs(),
          });
        })
        .catch((error) => {
          console.log('Error', error); // TODO: log error
          setSavingStatus((current) => ({
            ...current,
            active: false,
            error: true,
          }));
        });
    } else {
      api.createProduct(productData)
        .then((id) => {
          setProductId(id);
          updateExistingTableData(tableConfig, setTableConfig, id, productData);
          // Change from the 'create' path to the 'edit' path to reload product if refreshing.
          window.history.pushState('', '', `/dashboard/product-database/products/${id}/edit`);
          setSavingStatus({
            active: false,
            error: false,
            unsavedChanges: false,
            lastSaved: dayjs(),
          });
        })
        .catch((error) => {
          console.log('Error', error); // TODO: log error
          setSavingStatus((current) => ({
            ...current,
            active: false,
            error: true,
          }));
        });
    }
  };

  /**
   * Refresh SKU data from the server and update form state.
   * Used after SKU generation or any external SKU changes.
   */
  const refreshSkus = async () => {
    if (!productId) return;
    const updatedSkuData = await api.getProductSkus(productId);
    snapshotSkuPrices(updatedSkuData);
    withFormSync(() => {
      setCurrentSkus(updatedSkuData || []);
      const currentFormValues = form.getFieldsValue();
      form.setFieldsValue({
        ...currentFormValues,
        skus: updatedSkuData || [],
      });
    });
  };

  const onDelete = () => api.deleteProduct(productId).then(() => {
    // Clear unsaved changes to avoid showing alert when redirecting
    setSavingStatus((current) => ({
      ...current,
      unsavedChanges: false,
    }));
    history.push('/dashboard/product-database/products');
  });

  const onDeleteSku = async (skuKey) => {
    try {
      setSavingStatus((current) => ({
        ...current,
        active: true,
      }));

      await api.deleteProductSku(productId, skuKey);
      const updatedSkuData = await api.getProductSkus(productId);
      snapshotSkuPrices(updatedSkuData);
      withFormSync(() => {
        setCurrentSkus(updatedSkuData || []);
        const currentFormValues = form.getFieldsValue();
        form.setFieldsValue({
          ...currentFormValues,
          skus: updatedSkuData || [],
        });
      });
      setSavingStatus({
        active: false,
        error: false,
        unsavedChanges: false,
        lastSaved: dayjs(),
      });
    } catch (error) {
      console.log('Error deleting SKU:', error);
      setSavingStatus((current) => ({
        ...current,
        active: false,
        error: true,
      }));
    }
  };

  const onSaveFailed = useCallback((values) => {
    // console.log('onSaveFailed', values);
    // TODO: Show errors in tabs on left.
  }, []);

  const onValuesChange = useCallback(() => {
    if (!formReady.current) return;
    setSavingStatus((current) => {
      if (current.unsavedChanges) return current;
      return { ...current, unsavedChanges: true };
    });
  }, []);

  const onArtworkChange = ({ fieldName, newImageUrl }) => {
    if (fieldName === 'tapBadgeUrl') {
      setTapBadgeImageUrl(newImageUrl);
    }
    onValuesChange();
  };

  /** Data tab (barcodes + Foodstuffs NI/SI); mirror into form `skus` for Save. */
  const handleSkuDataFieldChange = useCallback((skuKey, fieldName, value) => {
    if (!canEditSku || skuKey == null) return;

    const matchesKey = (s) => s != null && String(s.key) === String(skuKey);

    setCurrentSkus((prev) => prev.map((s) => (
      matchesKey(s) ? { ...s, [fieldName]: value } : s
    )));

    const fv = form.getFieldsValue();
    const nextSkus = (fv.skus || []).map((s) => (
      matchesKey(s) ? { ...s, [fieldName]: value } : s
    ));
    form.setFieldsValue({ ...fv, skus: nextSkus });

    if (formReady.current) {
      setSavingStatus((current) => {
        if (current.unsavedChanges) return current;
        return { ...current, unsavedChanges: true };
      });
    }
  }, [canEditSku, form]);

  return (
    <EditorLayout
      id="product-editor-form"
      contextLabel="product"
      title={productName}
      thumbnail={tapBadgeImageUrl}
      form={form}
      initialValues={initialValues}
      enableDelete={isAdmin && !!productId}
      canSave={canSave}
      onDelete={onDelete}
      onSave={onSave}
      onSaveFailed={onSaveFailed}
      onValuesChange={onValuesChange}
      savingStatus={savingStatus}
      onTabChange={setActiveEditorTab}
      sidePanel={canViewActivity && productId ? (
        <ProductHistoryPanel
          productId={productId}
          skuId={historySku?.key ?? null}
          skuLabel={historySku ? skuTabDisplayName(productName, historySku) : null}
        />
      ) : null}
      additionalControls={(
        <Tooltip title={initialValues?.mondayId === null ? 'This product hasn\'t been linked to an item in Monday yet.' : undefined}>
          <Button
            type="primary"
            disabled={!initialValues?.mondayId}
            onClick={() => window.open(`https://garageproject.monday.com/boards/${MONDAY_PRODUCT_DATABASE_BOARD_ID}/pulses/${initialValues.mondayId}`, '_blank')}
          >
            View in Monday
          </Button>
        </Tooltip>
)}
    >
      <TabPane tab="About" key="about" className="padded-tab" forceRender>
        <About
          form={form}
          tapBadgeImageUrl={tapBadgeImageUrl}
          onNameChange={setProductName}
          canEdit={canSave}
        />
      </TabPane>
      <TabPane tab="SKUs" key="skus" className="product-editor-skus-outer-pane" forceRender>
        <Tabs
          className="product-editor-sku-subtabs"
          tabPosition="left"
          activeKey={activeSkuTab}
          onChange={setActiveSkuTab}
        >
          {skusOrderedForTabs?.length > 0 && skusOrderedForTabs.map((sku, index) => (
            <TabPane
              tab={(
                <span className="product-editor-sku-tab-inner">
                  {shouldShowSkuTabGroupHeader(sku, index, skusOrderedForTabs) && (
                    <div className="product-editor-sku-tab-group">
                      {getSkuContainerGroupLabel(sku)}
                    </div>
                  )}
                  <span className="product-editor-sku-tab-title">
                    <span className="product-editor-sku-tab-title-text">
                      {skuTabDisplayName(productName, sku)}
                    </span>
                    {sku.isLegacy && <Tag color="default" style={{ marginLeft: 6, fontSize: 10 }}>Legacy</Tag>}
                  </span>
                </span>
              )}
              key={sku.key}
              className="padded-tab"
            >
              <SkuManager
                selectedSkuKey={sku.key}
                onDeleteSku={onDeleteSku}
                onSynced={refreshSkus}
                canEditSku={canEditSku}
                savedSkuPricesRef={savedSkuPricesRef}
              />
            </TabPane>
          ))}
          <TabPane tab="+ Add SKU" key="new" className="padded-tab">
            <SkuManager
              selectedSkuKey="new"
              onDeleteSku={onDeleteSku}
              canEditSku={canEditSku}
              savedSkuPricesRef={savedSkuPricesRef}
            />
          </TabPane>
        </Tabs>
      </TabPane>
      <TabPane tab="Key dates" key="keyDates" className="padded-tab" forceRender>
        <KeyDates form={form} />
      </TabPane>
      {/* <TabPane tab="Units" key="units" forceRender>
            <Units form={form} productId={productId} />
          </TabPane> */}
      <TabPane tab="Brewing" key="brewing" className="padded-tab" forceRender>
        <Brewing />
      </TabPane>
      <TabPane tab="Artwork" key="artwork" className="padded-tab" forceRender>
        <Artwork
          form={form}
          productId={productId}
          saving={savingStatus.active}
          onArtworkChange={onArtworkChange}
        />
      </TabPane>
      <TabPane tab="Pricing" key="pricing" className="padded-tab" forceRender>
        <PricingSummary skus={currentSkus} productName={productName} />
      </TabPane>
      <TabPane tab="Data" key="data" className="padded-tab" forceRender>
        <BarcodesSummary
          skus={currentSkus}
          productName={productName}
          canEditSku={canEditSku}
          onSkuDataFieldChange={handleSkuDataFieldChange}
        />
      </TabPane>
      <TabPane tab="Extras" key="extras" className="padded-tab" forceRender>
        <Extras />
      </TabPane>
    </EditorLayout>
  );

  // return (!initialValues) ? (
  //   <FullPageLoader />
  // ) : (
  //   <Form
  //     id="product-editor-form"
  //     name="product-editor-form"
  //     form={form}
  //     initialValues={initialValues}
  //     onFinish={onSubmit}
  //     onFinishFailed={onSubmitFailed}
  //   >

  //     <Card
  //       id="product-editor-card"
  //       title={(
  //         <>
  //           <Title level={5}>
  //             {productName || <span className="unnamed-product-title">Untitled Product</span>}
  //           </Title>
  //           { productId ? (
  //             <Button
  //               danger
  //               type="outline"
  //               onClick={() => onDelete(productId)}
  //             >
  //               Delete product
  //             </Button>
  //           ) : (
  //             null
  //           )}
  //         </>
  //       )}
  //       size="small"
  //     >
  //       <Tabs tabPosition="left">
  // <TabPane tab="About" key="about" className="padded-tab" forceRender>
  //   <About
  //     form={form}
  //     tapBadgeImageUrl={tapBadgeImageUrl}
  //     onNameChange={setProductName}
  //   />
  // </TabPane>
  // <TabPane tab="Key dates" key="keyDates" className="padded-tab" forceRender>
  //   <KeyDates form={form} />
  // </TabPane>
  // {/* <TabPane tab="Units" key="units" forceRender>
  //   <Units form={form} productId={productId} />
  // </TabPane> */}
  // <TabPane tab="Artwork" key="artwork" className="padded-tab" forceRender>
  //   <Artwork form={form} onTapBadgeChanged={setTapBadgeImageUrl} />
  // </TabPane>
  // <TabPane tab="Pricing" key="pricing" forceRender>
  //   <Tabs tabPosition="left">
  //     <TabPane tab="Slab" key="slab" className="padded-tab" forceRender>
  //       <Pricing type="slab" />
  //     </TabPane>
  //     <TabPane tab="Case" key="case" className="padded-tab" forceRender>
  //       <Pricing type="case" />
  //     </TabPane>
  //     <TabPane tab="Keg" key="keg" className="padded-tab" forceRender>
  //       <Pricing type="keg" />
  //     </TabPane>
  //     <TabPane tab="Venue tap" key="venues" className="padded-tab" forceRender>
  //       <Pricing type="venues" />
  //     </TabPane>
  //   </Tabs>
  // </TabPane>
  // <TabPane tab="Barcodes" key="barcodes" forceRender>
  //   <Tabs tabPosition="left">
  //     <TabPane tab="Base" key="base" className="padded-tab" forceRender>
  //       <Barcodes type="base" />
  //     </TabPane>
  //     <TabPane tab="Slab" key="slab" className="padded-tab" forceRender>
  //       <Barcodes type="slab" />
  //     </TabPane>
  //     <TabPane tab="Case" key="case" className="padded-tab" forceRender>
  //       <Barcodes type="case" />
  //     </TabPane>
  //     <TabPane tab="Keg" key="keg" className="padded-tab" forceRender>
  //       <Barcodes type="keg" />
  //     </TabPane>
  //   </Tabs>
  // </TabPane>
  // <TabPane tab="Extras" key="extras" className="padded-tab" forceRender>
  //   <Extras />
  // </TabPane>
  //       </Tabs>
  //       { currentUser.admin && (
  //       <FixedButton
  //         htmlType="submit"
  //         loading={saving}
  //         icon={<CheckOutlined />}
  //       >
  //         Save
  //       </FixedButton>
  //       )}
  //     </Card>
  //   </Form>
  // );
};

export default ProductEditor;
